import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Preloader, Text } from '@common-fe/common-fe';

import { BackButton, Modal } from '@/components';
import useSubmitLettersOfMedicalNecessityPopup from '@/modules/MyDocuments/hooks/useSubmitLettersOfMedicalNecessityPopup';
import { LMNPopupStep } from '@/modules/MyDocuments/myDocuments.types';
import useLettersOfMedicalNecessityPopupStore from '@/modules/MyDocuments/stores/useLettersOfMedicalNecessityPopup.store';

import AssociateWithClaimsList from '../AssociateWithClaimsList/AssociateWithClaimsList';

interface Props {
  onSetVisible: (value: boolean) => void;
  setStep: (step: LMNPopupStep) => void;
  onSuccess?: (value: string) => void;
  setIsError?: (value: boolean) => void;
  onFileUploadError?: (error?: FileError) => void;
  isMobile?: boolean;
}

const AssociateWithClaimsPopup: React.FC<Props> = ({
  onSetVisible,
  setStep,
  onSuccess,
  setIsError,
  onFileUploadError,
  isMobile,
}) => {
  const { t } = useTranslation();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSkipLoading, setIsSkipLoading] = useState(false);
  const { handleCreateLMN, handleSuccessfullyCreated } = useSubmitLettersOfMedicalNecessityPopup({
    onFileUploadError,
  });
  const { sourceState, setSourceState } = useLettersOfMedicalNecessityPopupStore();
  const submitRef = useRef<() => Promise<void>>(undefined);
  const skipRef = useRef<() => Promise<void>>(undefined);

  const handleSetClaimsIds = useCallback(
    (claimIds: string[]) => {
      setSourceState({ ...sourceState, claimIds });
    },
    [sourceState, setSourceState]
  );

  const handleSkip = useCallback(async () => {
    setIsSkipLoading(true);
    try {
      const createdLMN = await handleCreateLMN({
        name: sourceState?.name,
        description: sourceState?.description,
        documentType: sourceState?.documentType,
        employeeId: sourceState?.employeeId,
        claimIds: [],
      });
      if (!createdLMN?.id) {
        return;
      }
      onSuccess?.(createdLMN?.id || '');
      handleSuccessfullyCreated();
      onSetVisible(false);
    } catch {
      setSourceState({
        ...sourceState,
        onError: async () => {
          await submitRef.current?.();
        },
      });
      setIsError?.(true);
    } finally {
      setIsSkipLoading(false);
    }
  }, [
    onSetVisible,
    handleCreateLMN,
    sourceState,
    setSourceState,
    handleSuccessfullyCreated,
    onSuccess,
    setIsError,
  ]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitLoading(true);
    try {
      const createdLMN = await handleCreateLMN({
        name: sourceState?.name,
        description: sourceState?.description,
        documentType: sourceState?.documentType,
        employeeId: sourceState?.employeeId,
        claimIds: sourceState?.claimIds,
      });
      if (!createdLMN?.id) {
        return;
      }
      setSourceState({
        ...sourceState,
        createdLMNId: createdLMN?.id,
        associatedClaims: createdLMN?.claims || [],
      });
      setStep(LMNPopupStep.CLAIMS_REVIEW_POPUP);
    } catch {
      setSourceState({
        ...sourceState,
        onError: async () => {
          await submitRef.current?.();
        },
      });
      setIsError?.(true);
    } finally {
      setIsSubmitLoading(false);
    }
  }, [setStep, handleCreateLMN, sourceState, setSourceState, setIsError]);

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  useEffect(() => {
    submitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    skipRef.current = handleSkip;
  }, [handleSkip]);

  return (
    <Modal
      isCloseButtonHidden={isMobile}
      testId="lmn-associate-with-claims"
      visible
      onSetVisible={onSetVisible}
      isFullScreen={isMobile}
    >
      {isMobile ? (
        <Box margin={{ bottom: 'spacing16' }}>
          <BackButton testId="lmn-popup" onClick={handleClose} title="Letters of Medical Necessity" />
        </Box>
      ) : null}

      <Text
        data-testId="lmn-associate-with-claims_title"
        size="2xl"
        textAlign={isMobile ? 'start' : 'center'}
        weight={700}
      >
        {t('Associate With Claims (Optional)')}
      </Text>

      <Box pad={{ horizontal: isMobile ? undefined : 'spacing24' }}>
        <Text
          data-testId="lmn-associate-with-claims_sub-title"
          margin={{ top: 'spacing12' }}
          size="medium"
          textAlign={isMobile ? 'start' : 'center'}
        >
          {t('These claims are pending documentation. Linking your LMN now can help speed up processing.')}
        </Text>
      </Box>

      <Box
        data-testId="lmn-associate-with-claims_wrapper"
        width={{ max: 'none' }}
        margin={{
          top: isMobile ? 'spacing16' : 'spacing24',
          horizontal: isMobile ? '-14px' : undefined,
        }}
        pad={isMobile ? 'spacing16' : 'spacing24'}
        background="module"
        round="moduleRound"
      >
        <AssociateWithClaimsList onSelectClaims={handleSetClaimsIds} isMobile={isMobile} />
      </Box>

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        gap="spacing12"
        direction="row"
        align="center"
        justify="end"
      >
        <Box flex={isMobile}>
          <AppButton
            testId="lmn-associate-with-claims_skip"
            width={isMobile ? '100%' : '160px'}
            buttonType="secondary"
            onClick={handleSkip}
            disabled={isSkipLoading}
          >
            {isSkipLoading ? <Preloader color="white" /> : t('Skip')}
          </AppButton>
        </Box>

        <Box flex={isMobile}>
          <AppButton
            testId="lmn-associate-with-claims_submit"
            width={isMobile ? '100%' : '160px'}
            buttonType="primary"
            onClick={handleSubmit}
            disabled={isSubmitLoading || !sourceState?.claimIds?.length}
          >
            {isSubmitLoading ? <Preloader color="white" /> : t('Select Claim')}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssociateWithClaimsPopup;
